import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCompress, faExpand, faXmark } from '@fortawesome/free-solid-svg-icons';
import aiMark from '../../assets/AI-Icon-SVG.svg';
import AssistantConversation from './AssistantConversation';
import ContactModal from './ContactModal';
import { useAssistantControls } from './AssistantProvider';
import useAssistantChat from '../../hooks/useAssistantChat';
import businessInfo, { getEmailHref, getPhoneHref } from '../../utils/businessInfo';

/**
 * Floating assistant widget: a launcher button plus a modal panel.
 *
 * Mounted once in MarketingLayout, so it is on every marketing page and never
 * on the portal shell, and the chat state survives both closing the panel and
 * navigating between pages.
 *
 * The launcher and the panel header both carry the AI mark
 * (assets/AI-Icon-SVG.svg): the answers are generated, and the mark says so at a
 * glance before a visitor reads anything. It is decorative in both places --
 * `alt=""` on the images -- so the accessible name stays the button's label,
 * which says what the button does rather than what the picture looks like. While
 * the panel is open the launcher's mark becomes a cross, because the launcher
 * stays on screen as a second exit.
 *
 * Ways back to the page -- a modal whose only exit is Escape is unusable for
 * pointer and touch visitors:
 *   1. the visible close button in the panel header
 *   2. clicking the scrim
 *   3. the launcher, which stays put and toggles closed
 *   4. Escape
 *
 * Accessibility follows the pattern already proven by MobileNav: focus moves
 * into the panel on open, Tab is trapped, Escape closes, and focus returns to
 * the launcher. Page scroll is locked through its own body class so the mobile
 * navigation drawer can never unlock it. Stacking keeps the two modals mutually
 * exclusive: the drawer's scrim covers the launcher.
 *
 * The contact modal ("Talk to a Person Instead") is a second layer, rendered here
 * rather than by the conversation, so the button that opened it stays mounted and
 * can take focus back when it closes. While it is open this panel's own Escape and
 * Tab handling stands down: one Escape closes one layer, and Tab cannot leave the
 * modal for the controls sitting behind its scrim. Closing the panel, or
 * navigating, closes the modal with it.
 *
 * The panel has two sizes: compact (the default) and expanded, which is 85% of
 * the viewport and full-screen on a phone, where the control is hidden because
 * it could do nothing. The size is deliberately not persisted and is reset when
 * the panel closes, so reopening never presents a surprise 45rem dialog.
 */
const PANEL_ID = 'assistant-panel';

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

const AssistantWidget = () => {
  const { isOpen, open, close } = useAssistantControls();
  const { messages, input, setInput, isSending, send, isConfigured } =
    useAssistantChat();

  const launcherRef = useRef(null);
  const panelRef = useRef(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isContactOpen, setIsContactOpen] = useState(false);
  const contactTriggerRef = useRef(null);
  const { pathname } = useLocation();

  // Close on navigation: a dialog left covering the page the visitor just moved to
  // is disorienting. The contact modal goes with it, since it can only be open over
  // this panel.
  useEffect(() => {
    setIsContactOpen(false);
    if (isOpen) close();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  // Lock page scroll while the panel is open.
  useEffect(() => {
    if (isOpen) document.body.classList.add('is-assistant-open');
    else document.body.classList.remove('is-assistant-open');

    return () => document.body.classList.remove('is-assistant-open');
  }, [isOpen]);

  // Escape closes and returns focus; Tab stays inside the panel.
  useEffect(() => {
    // The contact modal is a layer above this one and owns Escape and Tab while it
    // is open. Without this, one key press would close both layers and Tab would
    // walk out of the modal into the panel behind its scrim.
    if (!isOpen || isContactOpen) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setIsExpanded(false);
        close();
        launcherRef.current?.focus();
        return;
      }

      if (event.key !== 'Tab') return;

      const focusable = panelRef.current?.querySelectorAll(FOCUSABLE_SELECTOR);

      if (!focusable || focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    // Move focus in for keyboard and screen-reader users. The close button is
    // focused rather than the input so a mobile keyboard does not spring open
    // the moment the panel appears.
    const focusTimer = window.setTimeout(() => {
      panelRef.current?.querySelector('.assistant-widget__close')?.focus();
    }, 0);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      window.clearTimeout(focusTimer);
    };
  }, [isOpen, isContactOpen, close]);

  /**
   * Opens the contact modal, remembering what to put focus back on.
   *
   * The trigger lives in AssistantConversation, which is presentational and so
   * hands back no ref; the click's own element is the trigger, and the panel stays
   * mounted for as long as the modal can be open, so holding that node is enough --
   * and it leaves the shared Button primitive untouched.
   */
  const openContact = (event) => {
    contactTriggerRef.current = event.currentTarget;
    setIsContactOpen(true);
  };

  /** Closes the contact modal and hands focus back to the button that opened it. */
  const closeContact = () => {
    setIsContactOpen(false);
    contactTriggerRef.current?.focus();
  };

  /** Closes the panel -- and the contact modal with it -- and hands focus back. */
  const closeAndReturnFocus = () => {
    setIsExpanded(false);
    setIsContactOpen(false);
    close();
    launcherRef.current?.focus();
  };

  return (
    <>
      <button
        type="button"
        ref={launcherRef}
        className="assistant-launcher"
        onClick={isOpen ? closeAndReturnFocus : open}
        aria-expanded={isOpen}
        aria-controls={PANEL_ID}
        aria-label={
          isOpen
            ? 'Close the bookkeeping assistant'
            : 'Open the bookkeeping assistant'
        }
        // While open the panel is the dialog and owns the close control. The
        // launcher stays clickable as a second exit, but leaves the tab order
        // and the accessibility tree so it is not announced as a duplicate.
        aria-hidden={isOpen || undefined}
        tabIndex={isOpen ? -1 : undefined}
      >
        {isOpen ? (
          <FontAwesomeIcon icon={faXmark} aria-hidden="true" />
        ) : (
          <img src={aiMark} alt="" className="assistant-launcher__icon" />
        )}
      </button>

      {isOpen ? (
        <>
          <div
            className="assistant-widget__scrim"
            onClick={closeAndReturnFocus}
            aria-hidden="true"
          />

          <div
            id={PANEL_ID}
            className={`assistant-widget__panel${
              isExpanded ? ' assistant-widget__panel--expanded' : ''
            }`}
            role="dialog"
            aria-modal="true"
            aria-labelledby="assistant-panel-heading"
            ref={panelRef}
          >
            <div className="assistant-widget__header">
              <img src={aiMark} alt="" className="assistant-widget__mark" />
              <h2 className="assistant-widget__title" id="assistant-panel-heading">
                Bookkeeping assistant
              </h2>
              <button
                type="button"
                className="assistant-widget__expand"
                onClick={() => setIsExpanded((expanded) => !expanded)}
                aria-expanded={isExpanded}
                aria-controls={PANEL_ID}
                aria-label={
                  isExpanded
                    ? 'Collapse the assistant panel'
                    : 'Expand the assistant panel'
                }
              >
                <FontAwesomeIcon
                  icon={isExpanded ? faCompress : faExpand}
                  aria-hidden="true"
                />
              </button>
              <button
                type="button"
                className="assistant-widget__close"
                onClick={closeAndReturnFocus}
                aria-label="Close the bookkeeping assistant"
              >
                <FontAwesomeIcon icon={faXmark} aria-hidden="true" />
              </button>
            </div>

            <div className="assistant-widget__body">
              <AssistantConversation
                messages={messages}
                input={input}
                onInputChange={setInput}
                onSend={send}
                isSending={isSending}
                isConfigured={isConfigured}
                isExpanded={isExpanded}
                onOpenContact={openContact}
              />
            </div>
          </div>

          {isContactOpen ? (
            <ContactModal
              phone={businessInfo.phoneDisplay}
              phoneHref={getPhoneHref()}
              email={businessInfo.email}
              emailHref={getEmailHref()}
              onClose={closeContact}
              // No voice assistant exists yet, so onVoiceAssist is not passed and
              // the reserved slot renders nothing. Passing it here is the whole
              // change when one lands: this modal needs nothing else, and the phone
              // and the email stay exactly where they are.
            />
          ) : null}
        </>
      ) : null}
    </>
  );
};

export default AssistantWidget;
